import { Controller } from 'stimulus'

/**
 * Manages js for the amortization table (StimulusJS controller)
 *
 * @class AmortizationTableController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [ 'wrapper', 'table', 'tableContainer', 'outerContainer', 'button', 'arrow', 'dateColumn' ]

  /**
   * @function Controllers.AmortizationTableController.initialize
   * @memberof Controllers
   * @description Defines collapseListenerCallback for event listener usage
   *
   */
  initialize() {
    this.collapseListenerCallback = event => this._collapseTable(event)
  }

  /**
   * @function Controllers.AmortizationTableController.connect
   * @memberof Controllers
   * @description Adds event listener to outer container on connect.
   *
   */
  connect() {
    this.outerContainerTarget.addEventListener('table:collapse', this.collapseListenerCallback)
  }

  /**
   * @function Controllers.AmortizationTableController.teardown
   * @memberof Controllers
   * @description Removes event listener on outer container on teardown.
   *
   */
  teardown() {
    this.outerContainerTarget.removeEventListener('table:collapse', this.collapseListenerCallback)
  }

  /**
   * @function Controllers.AmortizationTableController.scroll
   * @memberof Controllers
   * @description Keeps tabs on the extent of the users scroll and removes the fuzzy white that implies scrollability
   *
   * @param {Event} event
   *
   * @return {undefined} manipulates DOM
   */
  scroll(event) {
    const tableWidth     = this.wrapperTarget.clientWidth
    const tableViewport  = this.tableTarget.clientWidth
    const scrollEnd      = tableWidth - tableViewport
    const extentScrolled = event.target.scrollLeft

    if (extentScrolled > 0) {
      this.arrowTarget.classList.add('amortization-table__scroll-right-arrow--hide')
    }

    if (extentScrolled >= scrollEnd) {
      this.tableTarget.classList.add('amortization-table--end')
    } else {
      this.tableTarget.classList.remove('amortization-table--end')
    }
  }

  /**
   * @function Controllers.AmortizationTableController.showMore
   * @memberof Controllers
   * @description collapses and expands table
   *
   * @param {Event} event
   *
   * @return {undefined} manipulates DOM
   */
  showMore(event) {
    const tableExpansionState = this.data.get('expansionState')

    if (tableExpansionState === 'collapsed') {
      this._expandTable()
    } else if (tableExpansionState === 'expanded') {
      this._collapseTable(event)
    }
  }

  /**
   * @function Controllers.AmortizationTableController.expandTable
   * @memberof Controllers
   * @description Expands table and calculates necessary height depending on number of rows
   *
   * @return {undefined} manipulates DOM
   */
  _expandTable() {
    const tableItemHeight = 48
    const tableHeaderRowHeight = 56
    const totalHeight = this.dateColumnTarget.children.length * tableItemHeight + tableHeaderRowHeight

    const heightPx = `${totalHeight}px`

    this.tableTarget.classList.add('amortization-table--show-all')
    this.tableTarget.style.height = heightPx
    this.data.set('expansionState', 'expanded')
    this.buttonTarget.innerText = 'SHOW LESS'
  }

  /**
   * @function Controllers.AmortizationTableController.collapseTable
   * @memberof Controllers
   * @description Collapses table and smooth scrolls for decent browers
   *
   * @param {Event} event
   *
   * @return {undefined} manipulates DOM
   */
  _collapseTable(event) {
    this.tableTarget.classList.remove('amortization-table--show-all')
    this.tableTarget.style.height = '536px'
    this.data.set('expansionState', 'collapsed')
    this.buttonTarget.innerText = 'SHOW MORE'

    if (event.type === 'click') {
      this.tableTarget.scrollIntoView({ behavior: 'smooth' })
    }
  }
}
